import { faBookCircleArrowRight } from "@fortawesome/pro-regular-svg-icons/faBookCircleArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Skeleton, styled, Typography } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcRequestErrorReport, MprcText } from "components";
import { ButtonClose } from "components/buttons";
import { IconButtonClose } from "components/iconButtons";
import { FeedItemDto, FeedType, PublicationType } from "generated/CoreApi_v1";
import { useMentionInterceptor } from "hooks/useMentionInterceptor";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { BehaviorMode, CardLocalContent, PublicationFeed } from "views/knowledge/components/card";
import { AuthorDialog } from "views/knowledge/components/card/behaviors/common/AuthorDialog";
import { CardContext } from "views/knowledge/components/card/CardBuilder";
import { getCardBehavior } from "views/knowledge/components/card/CardManager";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";

////////////////////////////////////////////////////////////////////////////////

const HeaderTitleHeight = "112px";

const MainContainer = styled("div")(
    ({ theme }) => ({
        width: "100%",
        minWidth: "716px",
        position: "relative",
        height: HeaderTitleHeight,
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)",
        overflow: "hidden"
    })
);

const ContentContainer = styled("div")(
    ({ theme }) => ({
        width: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: theme.palette.common.white,
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)",
        position: "absolute",
        zIndex: "1",
        height: HeaderTitleHeight
    })
);

const BackgroundThumbnail = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => (
        {
            backgroundImage: `url("${imageUrl}"), url("${imageUrl}")`,
            backgroundColor: theme.palette.common.black,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            position: "absolute",
            zIndex: "0",
            width: "100%",
            height: HeaderTitleHeight,
            filter: "blur(40px)",
            WebkitFilter: "blur(40px)",
        }
    )
);

////////////////////////////////////////////////////////////////////////////////

interface IHeaderProps {
    pub: FeedItemDto;
    enableTitleLink?: boolean;
    onBeforeLinkClick?: () => void;
}

const RenderDialogTitle: React.FC<IHeaderProps> = (props) => {
    const { pub, enableTitleLink = false, onBeforeLinkClick } = props;

    const { t } = useTranslation("knowledge");

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MainContainer>
                <BackgroundThumbnail imageUrl={pub.thumbnailUrl ? pub.thumbnailUrl : AppImages.PublicationTypeUnknowThumbnail} />
                <ContentContainer>
                    <Grid container direction="column" padding={2} wrap="nowrap" rowSpacing={1} >
                        <Grid>
                            {
                                enableTitleLink === false
                                    ? <Typography variant="h2" textTransform="unset" color="white">pub?.title</Typography>
                                    : (
                                        <Link
                                            onClick={onBeforeLinkClick}
                                            title={t("Publication.PublicationDialog.AccessTooltip")}
                                            to={KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.publicationType == PublicationType.Answer ? pub.answerDefinition?.questionId : pub.masterPublication?.publicationId)}
                                        >
                                            <Typography variant="h2" textTransform="unset" color="white">
                                                <FontAwesomeIcon icon={faBookCircleArrowRight} fixedWidth />&nbsp;{pub?.title}
                                            </Typography>
                                        </Link>
                                    )
                            }
                        </Grid>
                        <Grid>
                            <AuthorDialog />
                        </Grid>
                    </Grid>
                </ContentContainer>
            </MainContainer>
        </>
    );
};

/////////////////////////////////////////////////////////////////////////////////

const RenderDialogContent = ({ publication }: { publication: FeedItemDto; }) => {
    const { t } = useTranslation("knowledge");
    const mediaHeaderComponent = getCardBehavior("mediaHeader", BehaviorMode.Default, FeedType.ContentDialog, publication);
    const contentComponent = getCardBehavior("content", BehaviorMode.Default, FeedType.ContentDialog, publication);
    const mediaComponent = getCardBehavior("media", BehaviorMode.Default, FeedType.ContentDialog, publication);
    const attachComponent = getCardBehavior("attach", BehaviorMode.Default, FeedType.ContentDialog, publication);

    return publication.deletedOn != null
        ? <MprcText color="error">{t("Publication.PublicationDialog.ExcludedText")} {Formaters.performaDate(publication.deletedOn, { excludeTime: true })}</MprcText>
        : (
            <>
                {mediaHeaderComponent && mediaHeaderComponent({})}
                {contentComponent && contentComponent({})}
                {mediaComponent && mediaComponent({})}
                {attachComponent && attachComponent({})}
            </>
        );
};

/////////////////////////////////////////////////////////////////////////////////

const RenderDialogFooter = ({ publication }: { publication: FeedItemDto; }) => {
    const footerComponent = getCardBehavior("footer", BehaviorMode.Default, FeedType.ContentDialog, publication);

    return (
        <>
            {footerComponent && footerComponent({})}
        </>
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface RenderDialogProps {
    publication: FeedItemDto;
    onClose: () => void;
    onBeforeLinkClick?: () => void;
    enableTitleLink?: boolean;
    enableAuthorLink?: boolean;
    enableChannelLink?: boolean;
    onFeedItemChanged: (data: Partial<PublicationFeed>) => void;
}

const RenderDialogLoading = () => {

    return (
        <>
            <DialogTitle component="div" sx={{
                m: 0,
                p: 0,
                textTransform: "unset",
                "&.MuiDialogTitle-root+.MuiDialogContent-root": { padding: 2 }
            }}>
                <Box position="relative">
                    <MainContainer>
                        <BackgroundThumbnail imageUrl={AppImages.PublicationTypeUnknowThumbnail} />
                        <ContentContainer>
                            <Grid container direction="column" padding={2} wrap="nowrap" rowSpacing={1} >
                                <Grid>
                                    <Skeleton width="100%" height={22} variant="rectangular" sx={{ backgroundColor: "#f5f5f5" }} />
                                </Grid>
                                <Grid>
                                    <Grid container alignItems="center" spacing={1} width="100%">
                                        <Grid>
                                            <Skeleton width={44} height={44} variant="circular" sx={{ backgroundColor: "#f5f5f5" }} />
                                        </Grid>
                                        <Grid >
                                            <Skeleton width={250} height={32} variant="rectangular" sx={{ backgroundColor: "#f5f5f5" }} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </ContentContainer>
                    </MainContainer>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Skeleton width="100%" height={150} variant="rectangular" />
            </DialogContent>
        </>
    );
};


const RenderDialog = ({ publication, enableAuthorLink, enableTitleLink, enableChannelLink, onClose, onBeforeLinkClick, onFeedItemChanged }: RenderDialogProps) => {

    const mentionDialogId = `publicationViewerContainer_${publication.publicationId}`;
    const [mentionDialog] = useMentionInterceptor(mentionDialogId);

    const [localContent, setContent] = useState<CardLocalContent>({
        content: publication.content,
        contentLoadStatus: publication.contentLoadStatus,
        state: undefined
    });

    const feedItemHelper = useRef<FeedItemHelper>(new FeedItemHelper(publication!));

    const hasMedia = (publication.attachs && publication.presentation && publication.presentation.orderedItems.length > 0);

    return <CardContext.Provider value={
        {
            cardDivRef: null,
            pub: publication!,
            otherInfo: {
                enableAuthorLink,
                enableChannelLink,
            }
            ,
            cardOwnProps: {
                publicationId: "",
                presentation: {
                    autoplay: true,
                    currentAttachId: null,
                    currentIsVideo: null,
                    lighboxOpened: false
                },
                videoAutoPlay: true
            },
            localContent,
            helper: feedItemHelper.current,
            onFeedItemChanged: (data) => { onFeedItemChanged(data); },
            onReload: () => { },
            onUpdateLocalContent: (data: CardLocalContent) => setContent(data),
            onUpdateCardOwnProps: (data) => { }
        }}>
        <div id={mentionDialogId}>
            {mentionDialog}
            <DialogTitle component="div" sx={{
                m: 0,
                p: 0,
                textTransform: "unset",
                "&.MuiDialogTitle-root+.MuiDialogContent-root": { padding: 2 }
            }}>
                <Box position="relative">
                    <Box position="absolute" right="2px" zIndex="2">
                        <IconButtonClose color="white" size="large" onClick={onClose} />
                    </Box>
                    <RenderDialogTitle
                        pub={publication}
                        onBeforeLinkClick={onBeforeLinkClick}
                        enableTitleLink={enableTitleLink}
                    />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ overflow: hasMedia ? "hidden !important" : undefined }}>
                <RenderDialogContent publication={publication} />
            </DialogContent>
            <DialogActions>
                <RenderDialogFooter publication={publication} />
            </DialogActions>
        </div>
    </CardContext.Provider>;
};

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    publicationId: string;
    onClose: () => void;
    onBeforeLinkClick?: () => void;
    enableTitleLink?: boolean;
    enableAuthorLink?: boolean;
    enableChannelLink?: boolean;
}

export const PublicationViewerDialog = (props: IPros) => {
    const {
        publicationId,
        enableAuthorLink = false,
        enableChannelLink = false,
        enableTitleLink = true,
        onBeforeLinkClick,
        onClose
    } = props;

    const api = useCoreApi();
    const [publication, setPublication] = useState<FeedItemDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();

    ///
    const load = async (publicationId: string) => {
        try {
            const result = await api.v1.knowledgeFeed.getFeedItem({ publicationId });
            const record = result.data;

            setPublication(record);
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }
    }, []);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={onClose}
            >
                {
                    viewError
                        ? <>
                            <DialogContent><MprcRequestErrorReport error={viewError} /></DialogContent>
                            <DialogActions><ButtonClose onClick={onClose} /></DialogActions>
                        </>
                        : loading
                            ? <RenderDialogLoading />
                            : <RenderDialog
                                enableAuthorLink={enableAuthorLink}
                                enableChannelLink={enableChannelLink}
                                enableTitleLink={enableTitleLink}
                                publication={publication!}
                                onClose={onClose}
                                onBeforeLinkClick={onBeforeLinkClick}
                                onFeedItemChanged={(value) => setPublication({
                                    ...publication!,
                                    otherInfo: value.otherInfo ?? publication!.otherInfo,
                                    interaction: value.interaction ?? publication!.interaction,
                                })}
                            />
                }
            </Dialog>
        </>
    );
};
